import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useLang, scrollToId } from "@/lib/lang";
import { bookingHref } from "@/content";
import { EASE } from "./Reveal";

const Line = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay: 0.25 + i * 0.12, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const TiltCard = ({ card }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 14 });
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div style={{ perspective: 900 }} onMouseMove={move} onMouseLeave={() => { mx.set(0); my.set(0); }} className="absolute -bottom-8 -left-6 z-10 sm:-left-14">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 1, ease: EASE }} className="glass w-[250px] rounded-2xl p-5 sm:w-[280px]" data-testid="hero-fee-card">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{card.label}</p>
        <p style={{ transform: "translateZ(40px)" }} className="mt-3 font-display text-3xl font-semibold text-ink">{card.value}</p>
        <div className="my-4 h-px bg-line" />
        <p className="flex items-start gap-2 text-sm text-zinc-300"><span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-400 pulse-dot" />{card.note}</p>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const { t, lang } = useLang();
  const h = t.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const spot = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--sx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--sy", `${e.clientY - r.top}px`);
  };

  return (
    <section id="top" ref={ref} onMouseMove={spot} className="hero-spot relative min-h-[100svh] overflow-hidden pt-[72px]" data-testid="hero-section">
      <div className="grid-lines pointer-events-none absolute inset-0" />
      <div key={lang} className="relative mx-auto grid max-w-[1400px] gap-14 px-5 pb-20 pt-12 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pt-20">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="font-mono text-xs uppercase tracking-[0.22em] text-gold">{h.eyebrow}</motion.p>
          <h1 className="mt-8 font-display text-[2.6rem] font-semibold leading-[0.98] tracking-[-0.035em] text-ink sm:text-6xl lg:text-[5.4rem]" data-testid="hero-title">
            {h.lines.map((l, i) => <Line key={l} i={i}>{l}</Line>)}
            <Line i={h.lines.length} className="font-serif text-[0.92em] font-normal italic tracking-[-0.01em] text-gold">{h.accent}</Line>
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9, ease: EASE }} className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg" data-testid="hero-subtitle">{h.sub}</motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9, ease: EASE }} className="mt-10 flex flex-wrap gap-3">
            <a href={bookingHref()} target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="hero-book-btn">{h.primary} <ArrowUpRight size={18} /></a>
            <button onClick={() => scrollToId("calculator")} className="btn-ghost" data-testid="hero-calc-btn">{h.secondary} <ArrowDown size={16} /></button>
          </motion.div>
          <div className="mt-14 grid max-w-xl grid-cols-3 border-t border-line">
            {h.chips.map((c, i) => (
              <motion.div key={c.v} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 + i * 0.1 }} className="border-r border-line pr-3 pt-5 last:border-r-0 [&:not(:first-child)]:pl-4" data-testid={`hero-chip-${i}`}>
                <p className="font-display text-2xl font-semibold text-ink sm:text-3xl">{c.k}</p>
                <p className="mt-1 text-xs text-zinc-400 sm:text-sm">{c.v}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
        <div className="relative lg:col-span-5 lg:pl-6">
          <motion.div initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.4, delay: 0.4, ease: EASE }} className="relative aspect-[4/5] overflow-hidden rounded-[28px] border border-line">
            <motion.img style={{ y: imgY }} src="/img/pylons.jpg" alt="Energy transmission lines at dusk" className="absolute inset-0 h-[118%] w-full scale-110 object-cover object-center grayscale-[35%]" />
            <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/10 to-transparent" />
            <span className="absolute right-5 top-5 font-mono text-[11px] uppercase tracking-[0.2em] text-ink/80">EV / 2026</span>
          </motion.div>
          <TiltCard card={h.card} />
        </div>
      </div>
      <button onClick={() => scrollToId("pierderi")} className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 md:flex" data-testid="hero-scroll-btn">
        {h.scroll} <span className="scroll-line" />
      </button>
    </section>
  );
};
